import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import BannerImg from '../assets/images/banners/Banner1.webp'
import MortgageCalculatorWidget from '../components/mortgage/MortgageCalculatorWidget'
import FAQsSection from '../components/FAQsSection'
import Accordion from 'react-bootstrap/Accordion'

const MortgageCalculator = () => {
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        window.scrollTo(0, 0)

        // Handle responsive check for mobile views
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768)
        }

        handleResize() // Run initial check
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Mortgage Calculator - Estimate Repayments | 800 Homes</title>
                <meta
                    name="description"
                    content="Use 800 Homes free mortgage calculator to estimate your monthly repayments, minimum down payment, loan amount, and upfront costs."
                />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Mortgage Calculator - Estimate Repayments | 800 Homes" />
                <meta
                    property="og:description"
                    content="Use 800 Homes free mortgage calculator to estimate your monthly repayments, minimum down payment, loan amount, and upfront costs."
                />
                <meta property="og:url" content="https://800homes.ae/mortgage-calculator" />
                <meta property="og:site_name" content="800homes" />
                <meta property="og:image" content="https://800homes.ae/og.webp" />
                <link rel="canonical" href="https://800homes.ae/mortgage-calculator" />
            </Helmet>

            <Header />

            {/* HERO BANNER SECTION */}
            <section
                id="website-responsive"
                className="container-fluid common-banner shadow-800homes"
                style={{ backgroundImage: `url(${BannerImg})` }}
            >
                <div className="row justify-content-center align-items-center text-center h-100 w-100 m-0">
                    <div
                        className="col-11 col-lg-10"
                        style={{ transform: isMobile ? 'translateY(0px)' : 'translateY(-80px)' }}
                    >
                        {/* --- DESKTOP ONLY VERSION --- */}
                        <h1 className="d-none d-md-block lh-sm fw-600 banner-title text-uppercase text-white mx-auto">
                            Mortgage Calculator
                        </h1>
                        <p className="d-none d-md-block text-white fs-ss-18 fw-300 mt-3 mb-0 col-lg-8 mx-auto px-md-5">
                            Estimate your monthly repayments, loan size, and upfront costs for buying property in Dubai
                            or anywhere in the UAE.
                        </p>

                        {/* --- MOBILE ONLY VERSION (Smaller Sizing) --- */}
                        <h1 className="d-block d-md-none lh-sm fw-600 text-uppercase text-white mx-auto fs-3">
                            Mortgage Calculator
                        </h1>
                        <p
                            className="d-block d-md-none text-white fw-300 mt-2 mb-0 mx-auto px-2 small"
                            style={{ fontSize: '14px' }}
                        >
                            Estimate your monthly repayments, loan size, and upfront costs for buying property in Dubai
                            or anywhere in the UAE.
                        </p>
                    </div>
                </div>
            </section>

            <section className="pt-5 mt-md-4" />

            {/* CALCULATOR APP SECTION */}
            <section id="website-responsive" className="container-lg">
                <div className="row justify-content-center text-center mb-4 mb-md-5">
                    <div className="col-11 col-lg-9">
                        <h2 className="fs-ss-30 fw-600 text-white lh-sm">
                            Calculate your <span className="text-ss-primary">monthly repayment</span>
                        </h2>
                        <p className="fs-ss-16 text-grey px-md-5 fw-300 pt-3 lh-1-7em">
                            Use our free mortgage calculator to estimate monthly repayments, minimum down payment, and
                            purchase fees — based on UAE Central Bank rules. Whether you're a first-time buyer or
                            investing in a second property, get clarity on your numbers before speaking to a bank.
                        </p>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className="col-12 col-md-11">
                        <MortgageCalculatorWidget />
                    </div>
                </div>
            </section>

            <section className="pt-5 mt-md-5" />

            {/* FAQ SECTION */}
            <section id="website-responsive" className="container-lg text-white pb-5">
                <div className="fs-ss-30 fw-600 lh-sm text-center pb-4">Frequently asked questions</div>
                <div className="row justify-content-center">
                    <div className="col-11 col-md-9">
                        <Accordion defaultActiveKey={1} className="border-0">
                            <FAQsSection
                                EvenyKey={1}
                                Title="How is a mortgage loan calculated in the UAE?"
                                Para="Your monthly payment depends on the loan amount, interest rate, and loan term. Banks use a standard amortization formula: you pay the same amount each month, with more interest early on and more principal later. Our calculator applies this formula so you can see estimated repayments before speaking to a lender."
                            />
                            <FAQsSection
                                EvenyKey={2}
                                Title="What is the minimum down payment in Dubai?"
                                Para="UAE Central Bank rules set minimum down payments by buyer type: typically 15% for UAE nationals, 20% for UAE residents, and 25% for non-residents on their first property. Investment or second homes may require higher deposits. The calculator adjusts the minimum down payment when you change residency status."
                            />
                            <FAQsSection
                                EvenyKey={3}
                                Title="What upfront costs should I budget for?"
                                Para="Beyond your down payment, buyers usually pay Dubai Land Department transfer fees (around 4% of the property value), mortgage registration and processing fees, valuation, trustee fees, and broker commission. Our upfront costs section lists common estimates — your final bill may vary by property and bank."
                            />
                            <FAQsSection
                                EvenyKey={4}
                                Title="What interest rate should I use?"
                                Para="UAE home loan rates vary by bank, product, and whether the rate is fixed or linked to EIBOR. Fixed rates often fall between roughly 3% and 5% per year for introductory periods. Use our slider to model different scenarios, then contact 800 Homes for live bank offers."
                            />
                            <FAQsSection
                                EvenyKey={5}
                                Title="What is the maximum mortgage term in the UAE?"
                                Para="Most UAE banks offer home loans up to 25 years, subject to your age at maturity (often capped around 65–70). Shorter terms mean higher monthly payments but less total interest. Use the loan period slider to see how tenure affects your budget."
                            />
                        </Accordion>
                    </div>
                </div>
            </section>

            <section className="pt-5" />

            <Footer />
        </>
    )
}

export default MortgageCalculator
